import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, API_CONFIG } from '../../../config/api';
import { generateDeviceFingerprint } from '../../../shared/utils/deviceFingerprint';
import ErrorPopup from '../../../shared/components/ErrorPopup';
import { useAuth } from '../../../shared/hooks/useAuth';
import AccountantsList from './AccountantsList';

// 🔷 Card-ийн props төрөл
type CardProps = {
    title: string;
    desc: string;
    icon: string;
};

// ✅ Карт компонент
interface CardComponentProps extends CardProps {
    onClick?: () => void;
}

function Card({ title, desc, icon, onClick }: CardComponentProps) {
    const isDownloadCard = title.includes('Desktop') || title.includes('Desktop App');
    const isWebCard = title.includes('Web') || title.includes('NovaQ Web');
    const isAccountantCard = title.includes('Гэрээт нягтлан бодогч');
    
    return (
        <div 
            className={`group bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 hover:border-blue-200 flex flex-col min-h-[320px] ${onClick ? 'cursor-pointer' : ''}`}
            onClick={onClick}
        >
            <div className="text-4xl mb-6 transform group-hover:scale-110 transition-transform duration-300">{icon}</div>
            <h3 className="font-bold text-xl mb-3 text-gray-800 group-hover:text-blue-600 transition-colors duration-300">{title}</h3>
            <p className="text-gray-600 leading-relaxed mb-4 flex-grow">{desc}</p>
            
            <div className="mt-auto">
                {isDownloadCard && (
                    <a 
                        href={`https://github.com/btyalalt/NovaQ_Desk/raw/main/NovaQ_Desk.exe  `}    
                                                
                        
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-md"
                    >
                        <span>⬇️</span>
                        <span>Татах</span>
                    </a>
                )}
                
                {isWebCard && (
                    <a 
                        href="/"
                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-green-700 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-md"
                    >
                        <span>🌐</span>
                        <span>Нэвтрэх</span>
                    </a>
                )}

                {isAccountantCard && onClick && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClick();
                        }}
                        className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-md"
                    >
                        <span>👀</span>
                        <span>Харах</span>
                    </button>
                )}
            </div>
        </div>
    );
}

// ✅ Карт мэдээлэл
const cards: CardProps[] = [
    {
        title: 'Автомат санхүү систем',
        desc: 'Манай санхүүгийн систем нь санхүүгийн ажил гүйцгээг автоматжуулж, санхүүгийн хяналтыг хялбар болгоно.',
        icon: '📊',
    },
    {
        title: 'Санхүү, татварын зөвлөх үйлчилгээ',
        desc: 'Шинэ үеийн нягтлан бодогч нь санхүү, татварын эрсдлээс урьдчилан сэргийлэх зөвлөх мэргэжилтэн юм.',
        icon: '💲',
    },
    {
        title: 'Гэрээт нягтлан бодогч хайж байна уу ?',
        desc: 'NovaQ-тай хамтран ажилладаг нягтлан бодогч нараас санхүү татварын зөвлөгөө авах боломжтой.',
        icon: '🤝',
    },
    { 
        title: 'NovaQ Web', 
        desc: 'Веб хөтөч дээрээ шууд ашиглах боломжтой NovaQ систем. Хэрэглэгчийн нэвтрэх эрхээр системд нэвтэрч ашиглана уу.', 
        icon: '🌐' 
    },
    { 
        title: 'NovaQ Desktop', 
        desc: 'Таны компьютерт суулгаж ашиглах боломжтой NovaQ Desktop програм.', 
        icon: '💻' 
    },
];

// Dropdown component for login (moved outside HomePage)
interface LoginDropdownProps {
    style: React.CSSProperties;
    loginDropdownRef: React.RefObject<HTMLDivElement>;
    userId: string;
    setUserId: (v: string) => void;
    password: string;
    setPassword: (v: string) => void;
    selectedRole: string;
    setSelectedRole: (v: string) => void;
    handleLogin: () => void;
    onClose: () => void;
}
const LoginDropdown: React.FC<LoginDropdownProps> = ({
    style,
    loginDropdownRef,
    userId,
    setUserId,
    password,
    setPassword,
    selectedRole,
    setSelectedRole,
    handleLogin,
    onClose,
}) => {
    // Ролийн сонголтын хувилбарууд
    const roleOptions = [
        { value: 'Accountant', label: 'Нягтлан бодогч' }, 
        { value: 'SystemAdmin', label: 'Системийн админ' },
        { value: 'Director', label: 'Захирал' },
        { value: 'Employee', label: 'Ажилчин' }
    ];

    return (
    <div
        ref={loginDropdownRef}
        className="bg-white/95 backdrop-blur-md text-gray-900 shadow-2xl rounded-2xl p-8 border border-gray-200 animate-fadeIn"
        style={style}
    >
        <button
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none"
            onClick={onClose}
            aria-label="Close"
        >
            ×
        </button>
        <div className="text-center mb-6">
            <div className="text-3xl mb-2">🔐</div>
            <h3 className="font-bold text-xl text-gray-800">Нэвтрэх</h3>
            <p className="text-sm text-gray-600 mt-1">Системд нэвтэрнэ үү</p>
        </div>
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Нэвтрэх нэр</label>
                <input
                    type="text"
                    placeholder="Нэвтрэх нэрээ оруулна уу"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Нууц үг</label>
                <input
                    type="password"
                    placeholder="Нууц үгээ оруулна уу"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                />
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Роль сонгох</label>
                <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 bg-white"
                >
                    {roleOptions.map((role) => (
                        <option key={role.value} value={role.value}>
                            {role.label}
                        </option>
                    ))}
                </select>
            </div>
            <button
                onClick={handleLogin}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
                Нэвтрэх
            </button>
        </div>
    </div>
    );
};

// ✅ Гол компонент
export default function HomePage() {
    const [showLogin, setShowLogin] = useState(false);
    const [loginDropdownStyle, setLoginDropdownStyle] = useState<React.CSSProperties>({});
    const [loginReady, setLoginReady] = useState(false); // Only show dropdown when ready
    const [userId, setUserId] = useState('');
    const [password, setPassword] = useState('');
    const [selectedRole, setSelectedRole] = useState('Accountant');
    const [showErrorPopup, setShowErrorPopup] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showAccountantsList, setShowAccountantsList] = useState(false);
    const navigate = useNavigate();
    const loginBtnRef = useRef<HTMLButtonElement | null>(null);
    const loginDropdownRef = useRef<HTMLDivElement | null>(null);
    const { login } = useAuth();

    // Position the dropdown below and right-aligned to the button
    useEffect(() => {
        if (showLogin && loginBtnRef.current) {
            const rect = loginBtnRef.current.getBoundingClientRect();
            setLoginDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 8,
                left: rect.right - 320, // 320px is the dropdown width
                zIndex: 9999,
                minWidth: 320,
            });
            setLoginReady(true);
        } else {
            setLoginReady(false);
        }
    }, [showLogin]);

    // Close dropdown when clicking outside
    useEffect(() => {
        if (!showLogin) return;
        function handleClick(e: MouseEvent) {
            if (
                loginDropdownRef.current &&
                !loginDropdownRef.current.contains(e.target as Node) &&
                loginBtnRef.current &&
                !loginBtnRef.current.contains(e.target as Node)
            ) {
                setShowLogin(false);
            }
        }
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [showLogin]);



    const handleLogin = async () => {
        try {
            // Backend server IP address based on environment
            const BACKEND_SERVER = API_CONFIG.BASE_URL;
            
            console.log('🔧 Environment:', process.env.NODE_ENV);
            console.log('🔧 REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
            console.log('🔧 Backend Server:', BACKEND_SERVER);
            console.log('🔧 Backend Server length:', BACKEND_SERVER.length);
            console.log('🔧 Backend Server trimmed:', BACKEND_SERVER.trim());
            
            // Роль сонгосон эсэхийг шалгах
            if (!selectedRole) {
                setErrorMessage('Роль сонгоно уу');
                setShowErrorPopup(true);
                return;
            }

            console.log('🔍 Login attempt started');
            console.log('🔍 API endpoint:', API_ENDPOINTS.AUTH.LOGIN);
            console.log('🔍 Request payload:', { UserName: userId.trim(), PasswordHash: password.trim() });
            console.log('🔍 Selected role:', selectedRole);
            
            // 🧹 STEP 1: Clear old/bad token before anything - RE-ENABLED
            localStorage.removeItem('novaqToken');
    
            const deviceId = generateDeviceFingerprint();
            console.log('🔍 Device ID generated:', deviceId);
    
            // Create AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
            
            // Use direct connection for authentication based on selected role
            let loginEndpoint = '';
            
            switch (selectedRole) {
                case 'Accountant':
                    loginEndpoint = `${BACKEND_SERVER}${API_ENDPOINTS.AUTH.ACCOUNTANT}`;
                    break;
                case 'SystemAdmin':
                    loginEndpoint = `${BACKEND_SERVER}${API_ENDPOINTS.AUTH.SYSADMIN}`;
                    break;
                default:
                    loginEndpoint = `${BACKEND_SERVER}${API_ENDPOINTS.AUTH.ACCOUNTANT}`; // Default to accountant
            }
            
            // Clean up the URL to remove any extra spaces
            loginEndpoint = loginEndpoint.replace(/\s+/g, '').trim();
            
    
            
            console.log('🔍 Using login endpoint:', loginEndpoint, 'for role:', selectedRole);
            console.log('🔍 Login endpoint length:', loginEndpoint.length);
            console.log('🔍 Request headers:', {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-device-id': deviceId
            });
            console.log('🔍 Request body:', {
                UserName: userId.trim(),
                PasswordHash: password.trim()
            });
            
            const response = await fetch(loginEndpoint, {
                method: 'POST',
                mode: 'cors', // Explicitly enable CORS
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'x-device-id': deviceId, // RE-ENABLED
                },
                body: JSON.stringify({
                    UserName: userId.trim(),
                    PasswordHash: password.trim()
                }),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
    
            console.log('🔍 Response status:', response.status);
            console.log('🔍 Response headers:', Object.fromEntries(response.headers.entries()));
    
            const contentType = response.headers.get('content-type');
            console.log('🔍 Content-Type:', contentType);
            
            if (!contentType || !contentType.includes('application/json')) {
                console.error('🔍 Non-JSON response received');
                const textResponse = await response.text();
                console.error('🔍 Text response:', textResponse);
                throw new Error('Сервер JSON биш хариу илгээв');
            }
    
            const result = await response.json();
            console.log('🔍 Response result:', result);

            // Handle 401 Unauthorized specifically
            if (response.status === 401) {
                console.error('🔍 401 Unauthorized - Invalid credentials');
                console.error('🔍 401 Response details:', result);
                console.error('🔍 Endpoint used:', loginEndpoint);
                console.error('🔍 UserName sent:', userId.trim());
                console.error('🔍 Password length:', password.trim().length);
                if (!showErrorPopup) {
                    setErrorMessage(result.message || 'Нэвтрэх нэр эсвэл нууц үг буруу байна');
                    setShowErrorPopup(true);
                }
                return;
            }

            if (response.ok && result.success) {
                console.log('🔍 Login successful - result:', result);
                console.log('🔍 novaqToken from result:', result.novaqToken);
                console.log('🔍 User data from result:', result.data);
                
                // ✅ STEP 2: Use useAuth login hook to handle login properly
                const loginResult = await login(userId.trim(), password.trim(), result);
                
                if (!loginResult.success) {
                    console.error('❌ useAuth login failed:', loginResult.message);
                    if (!showErrorPopup) {
                        setErrorMessage(loginResult.message || 'Нэвтрэх үед алдаа гарлаа');
                        setShowErrorPopup(true);
                    }
                    return;
                }

                const user = loginResult.data;
                const userType = user.RoleName;
                console.log('🔍 Login Debug - User data:', user);
                console.log('🔍 Login Debug - RoleName:', userType);
                console.log('🔍 Login Debug - Selected Role:', selectedRole);
                
                // ✅ STEP 3: Verify token is stored in localStorage
                const storedToken = localStorage.getItem('novaqToken');
                console.log('🔍 Token stored in localStorage:', storedToken ? 'YES' : 'NO');
                console.log('🔍 Token length:', storedToken ? storedToken.length : 0);
    
                let path = '';
                let userRole = userType?.toLowerCase() || 'accountant'; // Server-ээс ирсэн роль ашиглах
    
                switch (userRole) {
                    case 'accountant':
                        path = '/main_accountant';
                        break;     
                    case 'sysadmin':
                        path = '/HomeScreen_AdminPanel';
                        break;
                    default:
                        console.log('⚠️ Unknown role type:', userType);
                        path = '/main_accountant';
                        userRole = 'accountant';
                        break;
                }
    
                console.log('🔍 Navigating to:', path);
                navigate(path, {
                    state: { userOid: user.Oid, userName: user.UserName },
                });
            } else {
                console.log('🔍 Login failed - response not ok or success false');
                console.log('🔍 Response ok:', response.ok);
                console.log('🔍 Result success:', result.success);
                console.log('🔍 Result message:', result.message);
                if (!showErrorPopup) {
                    setErrorMessage(result.message || 'Нэвтрэх амжилтгүй боллоо');
                    setShowErrorPopup(true);
                }
            }
        } catch (error: any) {
            console.error('🔍 Login error:', error);
            console.error('🔍 Error message:', error.message);
            console.error('🔍 Error stack:', error.stack);
            console.error('🔍 Error name:', error.name);
            console.error('🔍 Error type:', typeof error);
            
            // Check if it's an abort error (timeout)
            if (error.name === 'AbortError') {
                console.error('🔍 Request timed out after 10 seconds');
                if (!showErrorPopup) {
                    setErrorMessage('Серверт холбогдоход удаж байна. Дахин оролдоно уу.');
                    setShowErrorPopup(true);
                }
            } else if (error.message.includes('Failed to fetch') || error.message.includes('fetch')) {
                console.error('🔍 Network/CORS error - server might be down or CORS not configured');
                console.error('🔍 Trying to connect to backend server');
                if (!showErrorPopup) {
                    setErrorMessage('Серверт холбогдох боломжгүй байна. CORS эсвэл сүлжээний асуудал байж болзошгүй.');
                    setShowErrorPopup(true);
                }
            } else if (error.message.includes('TypeError')) {
                console.error('🔍 TypeError - likely network or CORS issue');
                if (!showErrorPopup) {
                    setErrorMessage('Серверт хандах боломжгүй. CORS тохиргоог шалгана уу.');
                    setShowErrorPopup(true);
                }
            } else {
                if (!showErrorPopup) {
                    setErrorMessage(error.message || 'Сүлжээний алдаа');
                    setShowErrorPopup(true);
                }
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
            {/* Толгой хэсэг */}
            <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-6 px-8 shadow-lg relative overflow-hidden z-30">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
                
                <div className="relative z-10 flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <img 
                            src={process.env.PUBLIC_URL + '/favicon.ico'} 
                            alt="NovaQ Logo" 
                            className="w-8 h-8" 
                            onError={(e) => {
                                // Prevent infinite loop by hiding the image if it fails to load
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                            }} 
                        />
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                            NovaQ
                        </h1>
                    </div>
                    
                    <nav className="space-x-8 select-none flex items-center">
                        <a href="#" className="hover:text-blue-200 transition-colors duration-300 font-medium">Эхлэл</a>
                        <a href="#" className="hover:text-blue-200 transition-colors duration-300 font-medium">Үйлчилгээ</a>
                        <div className="relative">
                            <button
                                ref={loginBtnRef}
                                className="bg-white/20 hover:bg-white/30 px-6 py-2 rounded-full transition-all duration-300 font-medium backdrop-blur-sm focus:outline-none"
                                onClick={() => setShowLogin((v) => !v)}
                            >
                                Нэвтрэх
                            </button>
                            {showLogin && loginReady && (
                                <LoginDropdown
                                    style={loginDropdownStyle}
                                    loginDropdownRef={loginDropdownRef}
                                    userId={userId}
                                    setUserId={setUserId}
                                    password={password}
                                    setPassword={setPassword}
                                    selectedRole={selectedRole}
                                    setSelectedRole={setSelectedRole}
                                    handleLogin={handleLogin}
                                    onClose={() => setShowLogin(false)}
                                />
                            )}
                        </div>
                    </nav>
                </div>
            </header>

            {/* Танилцуулга хэсэг */}
            <section className="text-center py-20 px-6 relative">
                {/* Background decoration */}
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-50/30 to-transparent"></div>
                
                <div className="relative z-10 max-w-4xl mx-auto">
                    <div className="mb-8">
                        <div className="text-6xl mb-6 animate-pulse">🚀</div>
                        <h2 className="text-5xl font-black mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                            Тун удахгүй…
                        </h2>
                    </div>
                    
                    <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto font-medium">
                        NovaQ технологи нь таны цаг хугацааг хэмнэж, бизнесийн ашиг орлогийг нэмэгдүүлж,
                        зардлыг багасгаж, хялбараар хянах боломжийг олгоно.
                    </p>
                    
                    <div className="mt-12 flex justify-center space-x-4">
                        <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-gray-700">Идэвхтэй</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-gray-700">Хурдан</span>
                        </div>
                        <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
                            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                            <span className="text-sm font-medium text-gray-700">Найдвартай</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Картууд */}
            <section className="max-w-7xl mx-auto px-6 pb-24">
                <div className="text-center mb-12">
                    <h3 className="text-3xl font-bold text-gray-800 mb-4">Үйлчилгээнүүд</h3>
                    <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {cards.map(({ title, desc, icon }, index) => {
                        const isAccountantCard = title.includes('Гэрээт нягтлан бодогч');
                        return (
                            <div key={index} className="transform hover:scale-105 transition-transform duration-300">
                                <Card 
                                    title={title} 
                                    desc={desc} 
                                    icon={icon}
                                    onClick={isAccountantCard ? () => setShowAccountantsList(true) : undefined}
                                />
                            </div>
                        );
                    })}
                </div>
            </section>

                        {/* Footer */}
            <footer className="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-8 px-6">
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-between items-center">
                        {/* Зүүн тал - Copyright */}
                        <div className="text-left">
                            <p className="text-gray-400">
                                © 2024 Би Ти Өү Софтлайн ХХК. Бүх эрх хуулиар хамгаалагдсан.
                            </p>
                        </div>
                        
                        {/* Баруун тал - Холбоо барих */}
                        <div className="text-right">
                            <div className="space-y-2">
                                <p className="text-gray-400 text-sm">Холбоо барих:</p>
                                <p className="text-blue-300">info@novaq.mn</p>
                                <p className="text-blue-300">+976 88034441</p>
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
            
            {/* Error Popup */}
            <ErrorPopup
                message={errorMessage}
                isVisible={showErrorPopup}
                onClose={() => setShowErrorPopup(false)}
                duration={5000}
            />

            {/* Accountants List Popup */}
            <AccountantsList
                isOpen={showAccountantsList}
                onClose={() => setShowAccountantsList(false)}
            />
        </div>
    );
}
